import { useQuery } from '@tanstack/react-query';
import { isEmpty, isEqual, map } from 'es-toolkit/compat';
import React from 'react';

import { getMultiplexPanels, getMultiplexPanelsQueryKey } from 'api/multiplexPanels';
import { MultiplexPanelsResponse } from 'interfaces/multiplexPanels';
import { Study } from 'interfaces/study';
import {
  applyPanelChoice,
  applyPreference,
  applySlideResults,
  buildSelectionModel,
  emptySelectionState,
  getModelSlides,
  getPanelRunIds,
  resetOverrides,
  resolveSelection,
  ResultsChoice,
  SelectionState,
  setPanelRunIds,
  setSlidesSelected,
} from 'services/export/workflowResultsSelection';
import { getRunLabels } from 'services/export/workflowRunLabels';
import { WorkflowExportKind, WorkflowResultsSourceSelection } from 'services/export/workflowExportSource';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useWorkflowResultsSource } from './useWorkflowResultsSource';

export interface WorkflowPanelsInput {
  panels?: MultiplexPanelsResponse;
  isLoading: boolean;
  // True while any request is in flight, including a refresh that keeps showing cached panels.
  isFetching: boolean;
  error?: unknown;
}

// The panels the Workflow's Cell typing & marker positivity step uses: the same request and cache entry.
export const useWorkflowStudyPanels = (study?: Study, enabled = true): WorkflowPanelsInput => {
  const { labId } = useCurrentLabId();
  const casesParams = { labId, studyId: study?.id, filters: { studyId: study?.id }, slidesMode: true };
  const query = useQuery({
    queryKey: getMultiplexPanelsQueryKey(casesParams),
    queryFn: ({ signal }) => getMultiplexPanels(casesParams, signal),
    enabled: enabled && Boolean(study?.id),
  });
  return {
    panels: query.data,
    isLoading: enabled && query.isLoading,
    isFetching: enabled && query.isFetching,
    error: query.error,
  };
};

const toPreference = ({ source, runIds }: WorkflowResultsSourceSelection): ResultsChoice =>
  source === 'run' ? { source, runIds: runIds ?? [] } : { source };

const toRunOverrides = (selections: Record<string, string[]>): SelectionState['panelChoices'] =>
  Object.fromEntries(
    Object.entries(selections).map(([panelKey, runIds]) => [panelKey, { source: 'run' as const, runIds }])
  );

// One Workflow export dialog's selection: the results preference (permissions, defaults and URL state live in
// useWorkflowResultsSource), then the panel and slide choices and unchecked slides, which belong to the open dialog.
export const useWorkflowResultsSelection = (
  kind: WorkflowExportKind,
  study: Study | undefined,
  options?: { ignoreStoredChoice?: boolean; enabled?: boolean }
) => {
  const ignoreStoredChoice = Boolean(options?.ignoreStoredChoice);
  const enabled = options?.enabled !== false;
  const panelsInput = useWorkflowStudyPanels(study, enabled);
  const source = useWorkflowResultsSource(kind, study, options);
  const {
    runs,
    runNames,
    canViewUnpublishedResults,
    select: selectPreference,
    setPanelRunSelections,
    panelRunSelections,
    clear: clearSource,
    selection: sourceSelection,
  } = source;
  // A stored panel run is an explicit run override when the global preference is not itself a specific run.
  // When the global preference is a specific run, the panel can still inherit that source and only the ids differ.
  const [state, setState] = React.useState<SelectionState>(() =>
    ignoreStoredChoice
      ? emptySelectionState
      : {
          ...emptySelectionState,
          panelRunIds: panelRunSelections,
          panelChoices: sourceSelection.source === 'run' ? {} : toRunOverrides(panelRunSelections),
        }
  );
  const inventoryUnavailable = source.hasRunsError && isEmpty(runs);

  const model = React.useMemo(
    () =>
      buildSelectionModel({
        panels: panelsInput.panels,
        runs,
        kind,
        resultsKnown: canViewUnpublishedResults,
        resultsInventory: inventoryUnavailable ? 'unavailable' : 'known',
        publishedExportEligible: source.publishedResultsEstablished,
      }),
    [
      panelsInput.panels,
      runs,
      kind,
      canViewUnpublishedResults,
      inventoryUnavailable,
      source.publishedResultsEstablished,
    ]
  );
  const preference = React.useMemo(() => toPreference(sourceSelection), [sourceSelection]);
  const selection = React.useMemo(() => resolveSelection(model, preference, state), [model, preference, state]);
  // A preference change that replaces stored panel runs clears them in the URL itself. Skip that one persist
  // so the pre-reset dialog state cannot write those runs back. A reset that does not change the stored ids
  // must not arm this flag.
  const skipPanelRunPersist = React.useRef(false);
  React.useEffect(() => {
    if (ignoreStoredChoice) return;
    if (skipPanelRunPersist.current) {
      skipPanelRunPersist.current = false;
      return;
    }
    if (isEqual(state.panelRunIds, panelRunSelections)) return;
    setPanelRunSelections(state.panelRunIds);
  }, [ignoreStoredChoice, panelRunSelections, setPanelRunSelections, state.panelRunIds]);
  const runLabels = React.useMemo(() => getRunLabels(runs, runNames), [runs, runNames]);
  const getRunLabel = React.useCallback((runId: string) => runLabels[runId] ?? 'Unnamed run', [runLabels]);

  const choosePreference = React.useCallback(
    (next: WorkflowResultsSourceSelection) => {
      // select() already clears stored panel runs. Suppress one persist only when this reset replaces stored ids,
      // so the effect is guaranteed to consume the flag. A second reset that is already empty must not leave the
      // flag set, or the next panel choice or Clear is dropped and a reload substitutes the newest run.
      skipPanelRunPersist.current = state.panelRunIds !== emptySelectionState.panelRunIds;
      selectPreference(next);
      setState(applyPreference());
    },
    [selectPreference, state.panelRunIds]
  );
  const choosePanel = React.useCallback(
    (panelKey: string, choice?: ResultsChoice) => {
      // A specific run starts with the panel's newest run, which is what the panel would export as its latest.
      const resolvedChoice =
        choice?.source === 'run' && isEmpty(choice.runIds)
          ? { source: 'run' as const, runIds: getPanelRunIds(model, panelKey).slice(0, 1) }
          : choice;
      setState((previous) => applyPanelChoice(model, previous, panelKey, resolvedChoice));
    },
    [model]
  );
  const setPanelRuns = React.useCallback(
    (panelKey: string, runIds: string[]) => setState((previous) => setPanelRunIds(model, previous, panelKey, runIds)),
    [model]
  );
  const chooseSlideResults = React.useCallback(
    (slideId: string, runIds?: string[]) => setState((previous) => applySlideResults(previous, slideId, runIds)),
    []
  );
  const setSelected = React.useCallback(
    (slideIds: string[], isSelected: boolean) =>
      setState((previous) => setSlidesSelected(previous, slideIds, isSelected)),
    []
  );
  const selectAllSlides = React.useCallback(
    () => setState((previous) => setSlidesSelected(previous, map(getModelSlides(model), 'slideId'), true)),
    [model]
  );
  const selectNoSlides = React.useCallback(
    () => setState((previous) => setSlidesSelected(previous, map(getModelSlides(model), 'slideId'), false)),
    [model]
  );
  const resetAllOverrides = React.useCallback(() => setState((previous) => resetOverrides(previous)), []);

  return {
    source,
    panelsInput,
    model,
    preference,
    selection,
    getRunLabel,
    choosePreference,
    choosePanel,
    setPanelRuns,
    chooseSlideResults,
    setSelected,
    selectAllSlides,
    selectNoSlides,
    resetAllOverrides,
    // Closing forgets the dialog's choices; the next opening starts from the normal defaults.
    close: clearSource,
  };
};

export type WorkflowResultsSelectionController = ReturnType<typeof useWorkflowResultsSelection>;
